import { create } from "zustand";

export interface FeedbackItem {
  id: string;
  type: string;
  source: { agentId: string; name: string };
  payload: any;
  timestamp: string;
  runId?: string;
}

export interface AgentActivity {
  count: number;
  lastActive: string;
  recentTimestamps: number[];
}

interface FeedbackStore {
  messages: FeedbackItem[];
  pendingInputs: any[];
  maxMessages: number;

  // Indexed access for swimlane/grouping
  messagesByAgent: Map<string, FeedbackItem[]>;
  messagesByType: Map<string, FeedbackItem[]>;
  agentActivity: Map<string, AgentActivity>;

  addMessage: (msg: FeedbackItem) => void;
  loadHistorical: (msgs: FeedbackItem[]) => void;
  addPendingInput: (input: any) => void;
  removePendingInput: (id: string) => void;
  clear: () => void;
}

export const useFeedbackStore = create<FeedbackStore>((set) => ({
  messages: [],
  pendingInputs: [],
  maxMessages: 500,
  messagesByAgent: new Map(),
  messagesByType: new Map(),
  agentActivity: new Map(),

  addMessage: (msg) =>
    set((s) => {
      const messages = [...s.messages, msg];
      if (messages.length > s.maxMessages) {
        messages.splice(0, messages.length - s.maxMessages);
      }

      // Update agent index
      const messagesByAgent = new Map(s.messagesByAgent);
      const agentKey = msg.source?.agentId ?? "unknown";
      const agentMsgs = messagesByAgent.get(agentKey) ?? [];
      agentMsgs.push(msg);
      if (agentMsgs.length > s.maxMessages) agentMsgs.splice(0, agentMsgs.length - s.maxMessages);
      messagesByAgent.set(agentKey, agentMsgs);

      // Update type index
      const messagesByType = new Map(s.messagesByType);
      const typeMsgs = messagesByType.get(msg.type) ?? [];
      typeMsgs.push(msg);
      if (typeMsgs.length > s.maxMessages) typeMsgs.splice(0, typeMsgs.length - s.maxMessages);
      messagesByType.set(msg.type, typeMsgs);

      // Update agent activity
      const agentActivity = new Map(s.agentActivity);
      const existing = agentActivity.get(agentKey) ?? { count: 0, lastActive: "", recentTimestamps: [] };
      const ts = new Date(msg.timestamp).getTime();
      const recentTimestamps = [...existing.recentTimestamps, ts].slice(-20);
      agentActivity.set(agentKey, {
        count: existing.count + 1,
        lastActive: msg.timestamp,
        recentTimestamps,
      });

      return { messages, messagesByAgent, messagesByType, agentActivity };
    }),

  loadHistorical: (msgs) =>
    set((s) => {
      const existingIds = new Set(s.messages.map((m) => m.id));
      const newMsgs = msgs.filter((m) => !existingIds.has(m.id));
      if (newMsgs.length === 0) return s;

      // Prepend historical (older) messages before existing (newer) ones
      const messages = [...newMsgs, ...s.messages];
      if (messages.length > s.maxMessages) {
        messages.splice(0, messages.length - s.maxMessages);
      }

      // Rebuild indexes
      const messagesByAgent = new Map<string, FeedbackItem[]>();
      const messagesByType = new Map<string, FeedbackItem[]>();
      const agentActivity = new Map<string, AgentActivity>();

      for (const msg of messages) {
        const agentKey = msg.source?.agentId ?? "unknown";
        if (!messagesByAgent.has(agentKey)) messagesByAgent.set(agentKey, []);
        messagesByAgent.get(agentKey)!.push(msg);

        if (!messagesByType.has(msg.type)) messagesByType.set(msg.type, []);
        messagesByType.get(msg.type)!.push(msg);

        const existing = agentActivity.get(agentKey) ?? { count: 0, lastActive: "", recentTimestamps: [] };
        const ts = new Date(msg.timestamp).getTime();
        agentActivity.set(agentKey, {
          count: existing.count + 1,
          lastActive: msg.timestamp > existing.lastActive ? msg.timestamp : existing.lastActive,
          recentTimestamps: [...existing.recentTimestamps, ts].slice(-20),
        });
      }

      return { messages, messagesByAgent, messagesByType, agentActivity };
    }),

  addPendingInput: (input) =>
    set((s) => ({ pendingInputs: [...s.pendingInputs, input] })),

  removePendingInput: (id) =>
    set((s) => ({
      pendingInputs: s.pendingInputs.filter(
        (p) => p.feedbackMessageId !== id,
      ),
    })),

  clear: () =>
    set({
      messages: [],
      pendingInputs: [],
      messagesByAgent: new Map(),
      messagesByType: new Map(),
      agentActivity: new Map(),
    }),
}));
